/**
 * Native OpenADS segmentation post-processing and metrics, ported for the
 * `*_desc-postproc` mask and `*_metrics.json` files.
 *
 *   DWI  `PostProcessingService.process` (services/segmentation/postprocessing.py):
 *        remove_small_objects_in_slice(min_size=5, 3x3 structure)
 *        -> binary_closing(ones(2,2,2)) -> binary_fill_holes
 *   PWI  `postprocess_hp_mask` (services/segmentation/postprocess_hp.py):
 *        binary_opening(ball r=1) -> binary_closing(ball r=1) -> fill holes
 *        -> keep components >= 100 voxels -> * brain mask
 *   Metrics `MetricsService.compute` (services/segmentation/metrics.py):
 *        dice = (2tp+1e-6)/(2tp+fp+fn+1e-6), precision/sensitivity likewise,
 *        sdr = pred non-empty.
 *
 * Volumes are x-fastest (Fortran order), matching `NiftiVolume.voxels`.
 */

type Dims3 = readonly [number, number, number] | readonly number[];

type Offset = [number, number, number];

/** scipy `generate_binary_structure(3, 1)` iterated `radius` times. */
function ballOffsets(radius: number): Offset[] {
  const out: Offset[] = [];
  for (let z = -radius; z <= radius; z++)
    for (let y = -radius; y <= radius; y++)
      for (let x = -radius; x <= radius; x++) if (Math.abs(x) + Math.abs(y) + Math.abs(z) <= radius) out.push([x, y, z]);
  return out;
}

/** `np.ones((2,2,2))` with scipy's default origin (centre index 1 → offsets -1..0). */
const CUBE2: Offset[] = [];
for (const z of [-1, 0]) for (const y of [-1, 0]) for (const x of [-1, 0]) CUBE2.push([x, y, z]);

function morph(src: Uint8Array, dims: Dims3, offsets: Offset[], mode: "dilate" | "erode"): Uint8Array {
  const [nx, ny, nz] = dims;
  const out = new Uint8Array(src.length);
  // Dilation uses the reflected structure (scipy semantics); erosion the structure itself.
  const offs = mode === "dilate" ? offsets.map(([x, y, z]) => [-x, -y, -z] as Offset) : offsets;
  for (let z = 0; z < nz; z++)
    for (let y = 0; y < ny; y++)
      for (let x = 0; x < nx; x++) {
        let hit = mode === "erode";
        for (const [dx, dy, dz] of offs) {
          const xx = x + dx;
          const yy = y + dy;
          const zz = z + dz;
          const inside = xx >= 0 && yy >= 0 && zz >= 0 && xx < nx && yy < ny && zz < nz;
          const v = inside ? src[xx + yy * nx + zz * nx * ny] : 0; // border_value = 0
          if (mode === "dilate" && v) {
            hit = true;
            break;
          }
          if (mode === "erode" && !v) {
            hit = false;
            break;
          }
        }
        out[x + y * nx + z * nx * ny] = hit ? 1 : 0;
      }
  return out;
}

const closing = (m: Uint8Array, d: Dims3, s: Offset[]) => morph(morph(m, d, s, "dilate"), d, s, "erode");
const opening = (m: Uint8Array, d: Dims3, s: Offset[]) => morph(morph(m, d, s, "erode"), d, s, "dilate");

/** scipy `binary_fill_holes` (3D, face connectivity): background not reachable from the border becomes 1. */
export function fillHoles(mask: Uint8Array, dims: Dims3): Uint8Array {
  const [nx, ny, nz] = dims;
  const outside = new Uint8Array(mask.length);
  const stack: number[] = [];
  const push = (i: number) => {
    if (!mask[i] && !outside[i]) {
      outside[i] = 1;
      stack.push(i);
    }
  };
  for (let z = 0; z < nz; z++)
    for (let y = 0; y < ny; y++)
      for (let x = 0; x < nx; x++)
        if (x === 0 || y === 0 || z === 0 || x === nx - 1 || y === ny - 1 || z === nz - 1) push(x + y * nx + z * nx * ny);
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % nx;
    const y = Math.floor(i / nx) % ny;
    const z = Math.floor(i / (nx * ny));
    if (x > 0) push(i - 1);
    if (x < nx - 1) push(i + 1);
    if (y > 0) push(i - nx);
    if (y < ny - 1) push(i + nx);
    if (z > 0) push(i - nx * ny);
    if (z < nz - 1) push(i + nx * ny);
  }
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < out.length; i++) out[i] = outside[i] ? 0 : 1;
  return out;
}

/** Connected-component sizes; `full` = 26/8-connectivity, else 6/4. Returns labels (0 = background). */
function label(mask: Uint8Array, dims: Dims3, full: boolean, sliceZ?: number): { labels: Int32Array; sizes: number[] } {
  const [nx, ny, nz] = dims;
  const labels = new Int32Array(mask.length);
  const sizes = [0];
  const zFrom = sliceZ ?? 0;
  const zTo = sliceZ ?? nz - 1;
  const neigh: Offset[] = [];
  const zr = sliceZ === undefined ? 1 : 0;
  for (let dz = -zr; dz <= zr; dz++)
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy && !dz) continue;
        if (!full && Math.abs(dx) + Math.abs(dy) + Math.abs(dz) > 1) continue;
        neigh.push([dx, dy, dz]);
      }
  for (let z = zFrom; z <= zTo; z++)
    for (let y = 0; y < ny; y++)
      for (let x = 0; x < nx; x++) {
        const start = x + y * nx + z * nx * ny;
        if (!mask[start] || labels[start]) continue;
        const id = sizes.length;
        let count = 0;
        const stack = [start];
        labels[start] = id;
        while (stack.length) {
          const i = stack.pop()!;
          count += 1;
          const cx = i % nx;
          const cy = Math.floor(i / nx) % ny;
          const cz = Math.floor(i / (nx * ny));
          for (const [dx, dy, dz] of neigh) {
            const xx = cx + dx;
            const yy = cy + dy;
            const zz = cz + dz;
            if (xx < 0 || yy < 0 || xx >= nx || yy >= ny || zz < zFrom || zz > zTo) continue;
            const j = xx + yy * nx + zz * nx * ny;
            if (mask[j] && !labels[j]) {
              labels[j] = id;
              stack.push(j);
            }
          }
        }
        sizes.push(count);
      }
  return { labels, sizes };
}

/** DWI `PostProcessingService.process`. */
export function postprocessDwi(mask: Uint8Array, dims: Dims3): Uint8Array {
  const [nx, ny, nz] = dims;
  const kept = new Uint8Array(mask.length);
  for (let z = 0; z < nz; z++) {
    const { labels, sizes } = label(mask, dims, true, z);
    for (let i = z * nx * ny; i < (z + 1) * nx * ny; i++) {
      const id = labels[i];
      if (id && sizes[id] >= 5) kept[i] = 1;
    }
  }
  return fillHoles(closing(kept, dims, CUBE2), dims);
}

/** PWI `postprocess_hp_mask` with the arguments `run_ads_pwi.py` passes. */
export function postprocessHp(mask: Uint8Array, dims: Dims3, brainMask?: Uint8Array | null): Uint8Array {
  const ball = ballOffsets(1);
  const filled = fillHoles(closing(opening(mask, dims, ball), dims, ball), dims);
  const { labels, sizes } = label(filled, dims, false);
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < out.length; i++) {
    const id = labels[i];
    if (id && sizes[id] >= 100 && (!brainMask || brainMask[i])) out[i] = 1;
  }
  return out;
}

export interface SegMetrics {
  dice: number | null;
  precision: number | null;
  sensitivity: number | null;
  sdr: number | null;
  pred_volume: number | null;
  true_volume: number | null;
}

/** `MetricsService.compute`; without a ground truth only the volume is known (native `_null_metrics`). */
export function segMetrics(pred: ArrayLike<number> | null, gt: ArrayLike<number> | null): SegMetrics {
  if (!pred) return { dice: null, precision: null, sensitivity: null, sdr: null, pred_volume: null, true_volume: null };
  let p = 0;
  for (let i = 0; i < pred.length; i++) if (pred[i] > 0.5) p += 1;
  if (!gt || gt.length !== pred.length) {
    return { dice: null, precision: null, sensitivity: null, sdr: null, pred_volume: p, true_volume: null };
  }
  let tp = 0;
  let t = 0;
  for (let i = 0; i < pred.length; i++) {
    const a = pred[i] > 0.5;
    const b = gt[i] > 0.5;
    if (b) t += 1;
    if (a && b) tp += 1;
  }
  const fp = p - tp;
  const fn = t - tp;
  return {
    dice: (2 * tp + 1e-6) / (2 * tp + fp + fn + 1e-6),
    precision: tp + fp > 0 ? (tp + 1e-6) / (tp + fp + 1e-6) : 1,
    sensitivity: tp + fn > 0 ? (tp + 1e-6) / (tp + fn + 1e-6) : 1,
    sdr: p > 0 ? 1 : 0,
    pred_volume: p,
    true_volume: t,
  };
}
